import type { CacheHeaders } from '#types/plugin/cache'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { resolveApiBaseUrl } from '#core/utils/api/resolveApiBaseUrl'
import apiClientFactory from '#root/api'
import { parseURL } from 'ufo'

/**
 * Retrieves headers for API requests.
 * @returns The headers object for API requests.
 * @category Utils
 */
export const useApiHeaders = () => {
  const { brandPrefix, clientApp } = useAppConfig().api
  return getApiHeaders(brandPrefix, clientApp, useCountryCode())
}

const getContentQuery = (env: string, target: string) => {
  const contentTarget = (env !== 'PROD' || target === 'production_test') && target
  return contentTarget ? { cms: contentTarget } : {} as any
}

/**
 * Builds base API headers for the given locale.
 *
 * @param locale Locale used to build the headers, resolved by the caller for route-specific
 * content variants (for example, `-es` support pages served under an `en-us` URL prefix).
 * @remarks Temporary workaround until locale derivation of `es-us` is fully implemented, so these pages resolve
 * the intended locale without per-route overrides.
 */
const setupAPIHeaders = (locale: ReturnType<typeof useLocale>) => {
  const settings = useApiHeaders()
  const correlationId = uuid()
  const { enableFApiProxy } = useFeatureFlags()
  const reqHeaders = useRequestHeaders()
  const ldPassthrough = reqHeaders?.['x-ld-passthrough'] || useFeatureFlagsPassthroughHeader().value
  const { authAccessKey, ssrTrustId } = useRuntimeConfig().api || {}
  const { isDev } = useRuntimeConfig().public

  return {
    ...settings,
    'Accept': 'application/json',
    'x-transaction-id': correlationId,
    'x-correlation-id': correlationId,
    'x-auth-access': authAccessKey || reqHeaders?.['x-auth-access'] || '',
    'vf-server-trust-id': ssrTrustId,
    'locale': (isDev || enableFApiProxy) ? locale : getApiLocale(locale),
    ...(reqHeaders?.['bg-testing'] && { 'bg-testing': reqHeaders['bg-testing'] }),
    ...(reqHeaders?.['x-bypass-maintenance'] && { 'x-bypass-maintenance': reqHeaders['x-bypass-maintenance'] }),
    ...(ldPassthrough && { 'x-ld-passthrough': ldPassthrough }),
    ...(isDev && reqHeaders?.['x-disable-human'] ? { 'x-disable-human': reqHeaders['x-disable-human'] } : {}),
  } as Record<string, string>
}

const setupCMSHeaders = (route: RouteLocationNormalizedLoaded) => {
  const reqHeaders = useRequestHeaders()
  const { authAccessKey, ssrTrustId, cmsPreviewToken } = useRuntimeConfig().api || {}
  const contentPreviewDate = route.query.previewDateRFC1123 as string

  return {
    ...(contentPreviewDate ? { 'X-Preview-Date': contentPreviewDate } : {}),
    'CoreMedia-PreviewAccessToken': cmsPreviewToken,
    'vf-server-trust-id': ssrTrustId,
    'x-auth-access': authAccessKey || reqHeaders?.['x-auth-access'] || '',
  } as Record<string, string>
}

export const useApi = (_route?: RouteLocationNormalizedLoaded, localeOverride?: ReturnType<typeof useLocale>) => {
  const locale = localeOverride || useLocale()
  const route = _route || useRoute()
  const usidCookie = useCookie('x-usid')
  const previewDateTimeCookie = useCookie('previewDateTime')
  const {
    enableAPIPreview,
    enableFApiProxy,
    enableFContentProxy,
    configApiDomains = {
      apiCutoverUrlSSR: undefined,
      cmsCutoverUrlSSR: undefined,
      apiBaseUrl: '',
      cmsBaseUrl: '',
      pathMappings: []
    },
    configCacheTagControlHeaders = {}
  } = useFeatureFlags()

  // Default base URLs (for requests without specific routing)
  const defaultApiBaseUrl = resolveApiBaseUrl('', configApiDomains, isServer())
  const cmsBaseUrl = (isServer() && configApiDomains.cmsCutoverUrlSSR) || configApiDomains.cmsBaseUrl

  let apiUrlSuffix = ''
  if (enableAPIPreview && (route.query.preview === 'true' || previewDateTimeCookie.value))
    apiUrlSuffix = '/pre'

  const { apiRoutePrefix, cmsApiRoutePrefix, isDev, targetEnv } = useRuntimeConfig().public

  const apiHeaders = setupAPIHeaders(locale)
  const cmsHeaders = setupCMSHeaders(route)

  const usidHeader = {
    // the x-usid cookie value is set on the Auth middleware
    // and cleared on sign-out on the Auth Store
    'x-usid': usidCookie.value || uuid()
  }

  // This is an object where every key from the API settings has an empty string value
  // and can be used to override the base headers where needed
  const clearedApiHeaders = Object.entries(apiHeaders).reduce((acc, [key]) => ({ ...acc, [key]: '' }), {})

  const cacheHeaders = useState<CacheHeaders>('cacheHeaders')

  return apiClientFactory({
    base: {
      baseURL: `${(isDev || enableFApiProxy) ? apiRoutePrefix : defaultApiBaseUrl}${apiUrlSuffix}`,
      headers: {
        ...apiHeaders,
        cookie: useRequestHeaders()?.cookie || ''
      },
      credentials: 'same-origin',
      query: {
        locale,
        ...(route.query.bypassAllCache === 'true' && { bypassAllCache: true })
      },
      onRequest({ request, options }) {
        if (!usidCookie.value)
          options.headers.delete('x-usid')

        if (options.headers.get('x-transaction-id')) {
          const id = uuid()
          options.headers.set('x-transaction-id', id)
          options.headers.set('x-correlation-id', id)
        }

        // Path-specific routing: resolve base URL per request if pathMappings configured
        // Note: When using /fapi proxy (dev or enableFApiProxy), the server proxy middleware
        // handles the actual routing, but we still resolve here for consistency
        if (configApiDomains.pathMappings?.length && !(isDev || enableFApiProxy)) {
          const requestUrl = typeof request === 'string' ? request : request.toString()
          const { pathname } = parseURL(requestUrl)
          const resolvedBaseUrl = resolveApiBaseUrl(pathname, configApiDomains, isServer())

          // Update base URL if path mapping changed it
          if (resolvedBaseUrl !== defaultApiBaseUrl) {
            options.baseURL = `${resolvedBaseUrl}${apiUrlSuffix}`
          }
        }
      }
    },
    authentication: {
      headers: usidHeader
    },
    content: {
      baseURL: (isDev || enableFContentProxy) ? cmsApiRoutePrefix : cmsBaseUrl,
      query: {
        ...getContentQuery(targetEnv, route.query.cms as string),
        removeNulls: true,
        ...((configCacheTagControlHeaders['Netlify-Cache-Tag']?.enabled || configCacheTagControlHeaders['X-Netlify-Cache-Tag']?.enabled) && { ct: true })
      },
      headers: {
        ...cmsHeaders
      },
      credentials: 'same-origin',
      onResponse(responseData) {
        // This callback is attached to CMS endpoints only
        // Save cache headers
        if (isServer()) {
          const cacheTags = [
            'Netlify-Cache-Tag',
            'X-Netlify-Cache-Tag',
            'Edge-Cache-Tag',
            'Netlify-CDN-Cache-Control',
            'X-Netlify-CDN-Cache-Control',
            'Akamai-Cache-Control'
          ]

          cacheTags.forEach((tag) => {
            const cacheHeader = responseData.response.headers.get(tag)

            if (cacheHeader && configCacheTagControlHeaders[tag]?.enabled) {
              // Split comma-separated cache tags and push individual tags
              if (tag.endsWith('Cache-Tag')) {
                const individualTags = [...new Set(cacheHeader.split(',').map((t) => t.trim()).filter(Boolean))]
                cacheHeaders.value[tag].push(...individualTags)
              }
              else {
                // For non-tag headers (like Cache-Control), push as-is
                cacheHeaders.value[tag].push(cacheHeader)
              }
            }
          })

          // Netlify strips the Netlify-Cache-Tag header so we merge it
          // with X-Netlify-Cache-Tag to ensure both are returned in the
          // response headers for downstream processing.
          const netlifyTags = cacheHeaders.value['Netlify-Cache-Tag'] || []
          const xNetlifyTags = cacheHeaders.value['X-Netlify-Cache-Tag'] || []
          if (netlifyTags.length || xNetlifyTags.length) {
            const merged = [...new Set([...xNetlifyTags, ...netlifyTags])]
            cacheHeaders.value['Netlify-Cache-Tag'] = merged
            cacheHeaders.value['X-Netlify-Cache-Tag'] = merged
          }
        }
      }
    },
    consumer: {
      headers: usidHeader
    },
    internal: {
      baseURL: apiRoutePrefix
    },
    wishlists: {
      headers: {
        ...usidHeader,
        // This is required for removing an item from the wishlist,
        // the API requires a content type, but the DELETE request has no body
        // so the client can't know to set it automatically
        'content-type': 'application/json'
      }
    },
    socialNative: {
      baseURL: 'https://photorankapi-a.akamaihd.net',
      headers: {
        ...clearedApiHeaders,
        Accept: 'application/json',
      }
    }
  })
}
